import {Container} from "../../Core/Container";
import {AssemblyBody, AssemblyItem} from "./AssemblyItem";
import {Dict} from "../../Core/Reflection";
import * as PIXI from "pixi.js";
import {Application} from "../../Application";

export abstract class Assembly<T extends string> extends Container {

    readonly items: AssemblyItem<T>[] = [];
    readonly slots: T[];

    constructor(slots: T[]) {
        super();
        this.slots = slots;
    }

    onLayout() {
        for (const slot of this.slots) {
            const items = this.getSlotItems(slot);
            items.forEach(it => it.visible = false);
            this.items.push(...items);
        }
        const bodies = this.getBodyItems();
        for (let i = 0; i < bodies.length; i++) {
            const body = bodies[i];
            for (const slot of this.slots) {
                if (slot === body.slot) continue;
                const items = this.getSlotItems(slot).filter(it => it.value === body.value);
                for (const item of items) {
                    body.setLimbPosition(slot, item.textureName, item);
                }
            }
        }
        for (const slot of this.slots) {
            this.setSlot(slot);
        }
    }

    setModel(model: Dict<T, number>) {
        if (model) {
            for (const slot of this.slots) {
                this.setSlot(slot, model[slot]);
            }
        }
    }

    getModel(): Dict<T, number> {
        const model: Dict<T, number> = {};
        for (const slot of this.slots) {
            model[slot] = this.getSlot(slot);
        }
        return model;
    }

    setSlot(slot: T, value?: number) {
        value = value ?? 1;
        const items = this.items.filter(it => it.slot === slot);
        items.forEach(it => it.visible = it.value === value);
        if (this.getBodyItems().some(it => it.slot === slot)) {
            this.updateAnchors();
        }
    }

    getSlot(slot: T) {
        return this.getSlotItems(slot).filter(it => it.visible)[0]?.value;
    }

    nextSlot(slot: T) {
        const items = this.items.filter(it => it.slot == slot);
        let index = items.indexOf(items.filter(it => it.visible)[0]);
        if (index < 0) {
            index = 0;
        } else {
            index++;
            if (index >= items.length) index = 0;
        }
        this.setSlot(slot, items[index].value);
    }

    updateAnchors() {
        const body = this.getBodyItems().filter(it => it.visible)[0];
        if (body) {
            for (const slot of this.slots) {
                if (slot !== body.slot) {
                    for (const item of this.getSlotItems(slot)) {
                        const p = body.getLimbPosition(slot, item.textureName);
                        if (p) {
                            item.x = p.x;
                            item.y = p.y;
                        }
                    }
                }
            }
        }
    }

    getRender(): string | undefined {

        const bounds = this.getLocalBounds();
        const matrix = new PIXI.Matrix();
        const s = 400 / bounds.height;
        const texture = PIXI.RenderTexture.create({width: bounds.width * s, height: bounds.height * s});

        matrix.scale(s / this.scale.x, s / this.scale.y);
        matrix.tx = -(this.x + bounds.x * this.scale.x) * matrix.a;
        matrix.ty = -(this.y + bounds.y * this.scale.y) * matrix.d;

        Application.instance.renderer.render(this, texture, true, matrix, false);
        return Application.instance.renderer.extract.base64(texture);
    }

    getEditableSlots() {
        return this.slots;
    }

    protected abstract getSlotItems(slot: T): AssemblyItem<T>[];

    protected abstract getBodyItems(): AssemblyBody<T>[];
}